import router from '@/router';
import { Log, UserManager, WebStorageStateStore } from 'oidc-client';
import config from '@/config';
import { auth as authConstants } from '@/store/constants';
import { OIDCErrorCodesThatRequireSignin } from '@/auth/constants';
import { parseToken } from '@/auth/utils';

const { UNSET_TOKEN_DATA, SET_TOKEN_DATA } = authConstants.mutations;

export default function AuthOAuthModule() {
  if (config.env === 'development') {
    Log.logger = console;
    Log.level = Log.DEBUG;
  }

  const userManager = new UserManager({
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    authority: config.auth.authority,
    client_id: config.auth.clientId,
    redirect_uri: `${config.frontendURL}/oauth/silentsignin/`,
    silent_redirect_uri: `${config.frontendURL}/oauth/silentsignin/`,
    response_type: 'id_token token',
    scope: 'openid',
    post_logout_redirect_uri: `${config.frontendURL}/`,
    loadUserInfo: true,
    automaticSilentRenew: true,
    monitorSession: true,
    filterProtocolClaims: true,
    clockSkew: 900,
  });

  const initialState = {
    tokenData: null, // TODO rename to accessToken
  };

  const authGetters = {
    accessToken: (state) => state.tokenData && state.tokenData.access_token,
    isAccessTokenRefreshable: () => true,
    accessTokenExp: (state) => state.tokenData && parseToken(state.tokenData.access_token).exp,
    user: (state) => state.tokenData && state.tokenData.profile,
    isUserAdmin: (state, getters) => getters.user && getters.user.simulixrole.includes('/admins'),
  };

  const actions = {
    async initialAutoLogIn({ commit, dispatch, state }) {
      if (state.tokenData) return;

      let initialLoading = true;
      dispatch('setLoaderVisiblity', { isVisible: true, content: 'Logging user in...' });

      try {
        userManager.events.addUserLoaded((user) => {
          commit(SET_TOKEN_DATA, user);

          if (initialLoading) {
            initialLoading = false;
            dispatch('setLoaderVisiblity', { isVisible: false });
          }
        });

        userManager.events.addSilentRenewError(async () => {
          await dispatch('logOut');
        });

        const user = await userManager.getUser();
        if (user && !user.expired) {
          userManager.events.load(user);
        } else {
          await dispatch('redirectToLoginPage');
        }
      } catch (e) {
        if (OIDCErrorCodesThatRequireSignin.includes(e.error)) {
          await dispatch('redirectToLoginPage');
        } else {
          dispatch('setErrorPageVisiblity', {
            isVisible: true,
            title: 'Log in error.',
            content: 'It was not possible to log in the user. Please try again later.',
          });
        }
      }
    },
    async processOAuthCallback({ commit, dispatch }) {
      dispatch('setLoaderVisiblity', { isVisible: true, content: 'Logging user in...' });

      try {
        const user = await userManager.signinCallback();
        commit(SET_TOKEN_DATA, user);
      } catch (err) {
        dispatch('setErrorPageVisiblity', {
          isVisible: true,
          title: 'Log in error.',
          content: 'It was not possible to log in the user. Please try again later.',
        });
      }

      dispatch('setLoaderVisiblity', { isVisible: false });
    },
    async logOut() {
      await userManager.removeUser();
      await userManager.signoutRedirect();
    },
    async redirectToLoginPage() {
      if (!router.history.current.meta.publicRoute) {
        await userManager.signinRedirect();
      }
    },
    redirectToDefaultPage({ getters }) {
      const isAdminRoute = router.history.current.matched.some(
        (match) => match.parent && match.parent.name === 'AdminWrapper'
      );

      if (router.history.current.name === 'OAuthSilentSignin') {
        router.replace(router.history.current.query.next || { name: 'ApplicationSelector' });
      }

      if (router.history.current.meta.publicRoute || (isAdminRoute && !getters.isUserAdmin)) {
        router.push(router.history.current.query.next || { name: 'ApplicationSelector' });
      }
    },
  };

  /* eslint-disable no-param-reassign */
  const mutations = {
    [SET_TOKEN_DATA](state, tokenData) {
      state.tokenData = tokenData;
    },
    [UNSET_TOKEN_DATA](state) {
      state.tokenData = null;
    },
  };
  /* eslint-enable no-param-reassign */

  return {
    state: initialState,
    getters: authGetters,
    actions,
    mutations,
  };
}
